import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, MapPin, ShieldCheck, Loader2 } from "lucide-react";
import api, { usd, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => { setListing(r.data); setAmount(String(r.data.price)); })
      .catch(() => nav("/marketplace"));
  }, [id, nav]);

  const isOwner = user && listing && user.id === listing.seller_id;

  const submitOffer = async () => {
    if (!user) return nav("/auth?mode=login");
    setSubmitting(true);
    try {
      await api.post("/offers", { listing_id: id, amount: parseFloat(amount), message });
      toast.success("Offer sent to the broker desk.");
      setMessage("");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const reserve = async () => {
    if (!user) return nav("/auth?mode=login");
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { listing_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPaying(false);
    }
  };

  if (!listing) return <div className="pt-40 text-center font-mono text-[#8C6552]">LOADING…</div>;

  const deposit = Math.round(listing.price * 0.1);

  return (
    <div className="relative z-10 pt-28 md:pt-32 pb-24 max-w-[1400px] mx-auto px-5 md:px-10">
      <Link to="/marketplace" className="mono-label inline-flex items-center gap-2 mb-8 hover:text-[#EFE4D2] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Marketplace
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <motion.div className="lg:col-span-7" initial={{ clipPath: "inset(0 100% 0 0)" }} animate={{ clipPath: "inset(0 0% 0 0)" }}
                    transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}>
          <div className="border border-[#5A2A24] relative">
            <img src={listing.image} alt={listing.title} className="w-full aspect-[4/3] object-cover" />
            <div className="absolute top-4 left-4 mono-label bg-[#1E070A]/80 px-3 py-1 backdrop-blur-sm">{listing.category}</div>
            {listing.status === "reserved" && <div className="absolute top-4 right-4 mono-label bg-[#C0894F] text-white px-3 py-1">RESERVED</div>}
          </div>
        </motion.div>

        <div className="lg:col-span-5">
          <div className="mono-label mb-3">{listing.manufacturer}</div>
          <h1 className="font-display uppercase text-4xl sm:text-5xl leading-none mb-4">{listing.title}</h1>
          <div className="font-mono text-[#C0894F] text-3xl mb-6">{usd(listing.price)}</div>

          <div className="grid grid-cols-2 gap-px bg-[#5A2A24] border border-[#5A2A24] mb-6">
            {[["Caliber", listing.caliber], ["Condition", listing.condition], ["Category", listing.category], ["Location", listing.location]].map(([k, v]) => (
              <div key={k} className="bg-[#2C0D12] p-4">
                <div className="mono-label mb-1">{k}</div>
                <div className="font-mono text-sm flex items-center gap-1">{k === "Location" && <MapPin className="w-3 h-3 text-[#C0894F]" />}{v}</div>
              </div>
            ))}
          </div>

          <p className="text-[#C7A98F] leading-relaxed mb-4">{listing.description}</p>
          <div className="flex items-center gap-2 text-xs font-mono text-[#8C6552] mb-8">
            <ShieldCheck className="w-4 h-4 text-[#9DB562]" /> Sold by {listing.seller_name} · FFL transfer required
          </div>

          {isOwner ? (
            <div className="border border-[#5A2A24] p-6 font-mono text-sm text-[#C7A98F]">This is your listing. Manage it from your dashboard.</div>
          ) : (
            <div className="space-y-6">
              <div className="border border-[#5A2A24] p-6 bg-[#2C0D12]">
                <div className="mono-label mb-4">Reserve with deposit</div>
                <p className="text-sm text-[#C7A98F] mb-4">Secure this piece with a <span className="text-[#EFE4D2] font-mono">{usd(deposit)}</span> deposit (10%). Balance settled at FFL transfer.</p>
                <button onClick={reserve} disabled={paying || listing.status === "reserved"} data-testid="reserve-btn" className="btn-tactical w-full disabled:opacity-50">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : listing.status === "reserved" ? "Already Reserved" : `Reserve — ${usd(deposit)} Deposit`}
                </button>
              </div>

              <div className="border border-[#5A2A24] p-6">
                <div className="mono-label mb-4">Make an offer</div>
                <label className="mono-label block mb-1">Your offer (USD)</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="offer-amount" className="field mb-4" />
                <label className="mono-label block mb-1">Message to seller</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="offer-message"
                  placeholder="Include trades, questions, timeline…" className="field !h-auto py-3 mb-4 resize-none" />
                <button onClick={submitOffer} disabled={submitting} data-testid="submit-offer-btn" className="btn-ghost w-full disabled:opacity-50">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Offer"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
